import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SaveIcon from '@mui/icons-material/Save'
import { usersApi, UserCreate } from '@shared/api-client'
import { useInstance } from '../contexts/InstanceContext'
import { provisioningApi } from '../services/provisioningApi'
import { normalizeInstanceConfig } from '../utils/instanceApi'
import { createInstanceUser } from '../utils/instanceApi'

export default function AddUser() {
  const navigate = useNavigate()
  const { currentInstance, instances } = useInstance()
  const [loading, setLoading] = useState(false)
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>(currentInstance?.id || '')
  const [formData, setFormData] = useState<UserCreate>({
    email: '',
    password: '',
    full_name: '',
    is_active: true,
  })

  useEffect(() => {
    if (currentInstance && !selectedInstanceId) {
      setSelectedInstanceId(currentInstance.id)
    }
  }, [currentInstance])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Always use the selected instance if one is selected
      if (selectedInstanceId) {
        const instanceData = await provisioningApi.getInstance(selectedInstanceId)
        const instance = normalizeInstanceConfig(instanceData)
        await createInstanceUser(instance, formData)
        navigate(`/organizations/${selectedInstanceId}`)
      } else {
        // Fallback to current instance if no selection
        await usersApi.create(formData)
        navigate('/users')
      }
    } catch (error: any) {
      console.error('Failed to create user:', error)
      alert(error.response?.data?.detail || 'Failed to create user')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field: keyof UserCreate) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.type === 'checkbox' 
      ? (e.target as HTMLInputElement).checked 
      : e.target.value
    setFormData({ ...formData, [field]: value })
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/users')}
        >
          Back to Users
        </Button>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Add New User
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Organization *</InputLabel>
                <Select
                  value={selectedInstanceId}
                  label="Organization *"
                  onChange={(e) => setSelectedInstanceId(e.target.value)}
                  required
                >
                  {instances.length === 0 ? (
                    <MenuItem value="" disabled>
                      No organizations available
                    </MenuItem>
                  ) : (
                    instances.map((instance) => (
                      <MenuItem key={instance.id} value={instance.id}>
                        {instance.name} ({(instance as any).organization_domain || (instance as any).organizationDomain || 'N/A'})
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={formData.password}
                onChange={handleChange('password')}
                required
                inputProps={{ minLength: 8 }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.full_name}
                onChange={handleChange('full_name')}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_active ?? true}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button onClick={() => navigate('/users')}>Cancel</Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={loading || !formData.email || !formData.password}
                >
                  {loading ? 'Creating...' : 'Create User'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  )
}

